import { useToast } from '@lifebit-ai/lds'
import { UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { HttpStatusCodes } from 'enums'

import { getParticipantFileSignedUrl } from 'services/results'
import { ResultsQueryKeys } from 'services/results/queryKeys'
import { ApiErrorResponse } from 'services/types'
import { download } from 'utils/download'

interface UseDownloadParticipantFileArgs {
  resultId: string
}

export const PARTICIPANT_FILE_DOWNLOAD_SUCCESS_MESSAGE = 'Download completed'

export const PARTICIPANT_FILE_DOWNLOAD_UNAVAILABLE_MESSAGE =
  'The person ID file is no longer available to download. The request has been refreshed with its current state.'

export const PARTICIPANT_FILE_DOWNLOAD_ERROR_MESSAGE =
  'Your download failed. If this issue persists, please contact your administrator.'

export const useDownloadParticipantFile = (
  options?: UseMutationOptions<void, ApiErrorResponse, UseDownloadParticipantFileArgs>
) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { onSuccess, onError, ...restOptions } = options ?? {}

  return useMutation<void, ApiErrorResponse, UseDownloadParticipantFileArgs>({
    mutationFn: async ({ resultId }) => {
      const { signedUrl, fileName } = await getParticipantFileSignedUrl({ resultId })

      download(signedUrl, fileName, '_blank')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      toast({ message: PARTICIPANT_FILE_DOWNLOAD_SUCCESS_MESSAGE, type: 'success' })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      /**
       * A 409 means the file is not downloadable, either because it is still being prepared or
       * because preparation failed. Either way the cached `Ready` status is stale, so the request
       * is refetched to rebuild the row from its real state instead of inviting a doomed retry.
       */
      const isUnavailable = error.response?.status === HttpStatusCodes.Conflict

      if (isUnavailable) {
        queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultDetails(variables.resultId) })
      }

      toast({
        message: isUnavailable
          ? PARTICIPANT_FILE_DOWNLOAD_UNAVAILABLE_MESSAGE
          : PARTICIPANT_FILE_DOWNLOAD_ERROR_MESSAGE,
        type: isUnavailable ? 'warning' : 'error'
      })
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions
  })
}
